import { Directive, ElementRef, OnDestroy, OnInit, Optional } from '@angular/core';

import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { PopupComponent } from '@progress/kendo-angular-popup';

import { ModalComponent } from '@app/shared/dialogs/modal/modal.component';
import { BrowserDetectionService } from '@app/services/browser-detection.service';
import { GlobalEventService, ModalWindowMessages } from '@app/services/global-event.service';
import { ModalDomRect, OperationType } from '@app/shared/utils/dom-manipulate';
import { LocalStorage } from '@app/session/local-storage';

const modalCounterKey = 'modalCounter';

@Directive({
  standalone: false,
  selector: 'kendo-popup, modal, [tiModalGeometryChange]',
})
export class ModalGeometryChangeDirective implements OnInit, OnDestroy {
  private subs: Subscription;
  private type: 'modal' | 'popup' = null;
  private counter: number;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly globalEventService: GlobalEventService,
    @Optional() private readonly popupComponent: PopupComponent,
    @Optional() private readonly modalComponent: ModalComponent,
  ) {}

  ngOnInit(): void {
    if (!BrowserDetectionService.isIE()) {
      return;
    }

    this.counter = LocalStorage.get(modalCounterKey) ? JSON.parse(LocalStorage.get(modalCounterKey)) : 0;
    this.counter = this.counter < 0 ? 0 : this.counter;
    LocalStorage.set(modalCounterKey, JSON.stringify(++this.counter));

    this.type = !(!this.popupComponent || this.elementRef.nativeElement !== this.popupComponent.container.nativeElement)
      ? 'popup'
      : this.modalComponent &&
          this.modalComponent &&
          this.elementRef.nativeElement === this.modalComponent.elementRef.nativeElement
        ? 'modal'
        : null;

    const cmp =
      this.type === 'popup' && this.popupComponent
        ? this.popupComponent
        : this.type === 'modal' && this.modalComponent
          ? this.modalComponent
          : null;

    if (!cmp) {
      return;
    }

    this.subs = new Subscription();

    this.subs.add(
      cmp.open.subscribe((data) => {
        setTimeout(() => this.broadcastChanges('create'), 10);
      }),
    );

    this.subs.add(
      cmp.positionChange.pipe(debounceTime(500)).subscribe((data) => {
        setTimeout(() => this.broadcastChanges('changePos'), 10);
      }),
    );

    this.subs.add(
      cmp.close.subscribe((data) => {
        setTimeout(() => this.broadcastChanges('delete'), 10);
      }),
    );
  }

  ngOnDestroy(): void {
    this.counter = LocalStorage.get(modalCounterKey) ? JSON.parse(LocalStorage.get(modalCounterKey)) : 0;
    LocalStorage.set(modalCounterKey, JSON.stringify(--this.counter));

    this.subs && this.subs.unsubscribe();
    setTimeout(() => this.broadcastChanges('delete'), 10);
  }

  private broadcastChanges(operation: OperationType) {
    const el = this.getElement();

    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();

    if (!rect) {
      return;
    }

    this.globalEventService.broadcast(ModalWindowMessages.modalWindowGeometryChange, <ModalDomRect>{
      id: `${this.type}-${this.counter}`,
      operation: operation,
      modalRect: {
        x1: rect.left,
        y1: rect.top,
        x2: rect.right,
        y2: rect.bottom,
      },
    });
  }

  private getElement() {
    if (!this.elementRef || !this.elementRef.nativeElement || !this.elementRef.nativeElement || !this.type) {
      return null;
    }

    let el =
      this.type === 'popup'
        ? this.elementRef.nativeElement
        : this.elementRef.nativeElement.querySelectorAll('div.modal-element')[0];

    //#region Workarounds
    if (this.popupComponent && el.classList.contains('user-profile-details')) {
      el = el.querySelectorAll('div.user-profile-details')[0];
    }
    //#endregion

    if (!el || !el.getBoundingClientRect) {
      return null;
    }

    return el;
  }
}
